import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { specimenStatementQueryOptions } from '../api/zonalStatement.api';
import { getSpecimenStatementColumns } from '../components/StatementColumns';
import { getSpecimenStatCards } from '../utils/statementStatCards';
import { getStatementTitle } from '../utils/statementTitle';

const route = getRouteApi('/_auth/dashboards/zonal-statement/$userId/specimen');

const STATEMENT_NAME = 'Specimen Statement';

interface SpecimenStatementPageProps {
  userId: number;
}

export function SpecimenStatementPage({ userId }: SpecimenStatementPageProps) {
  const search = route.useSearch();
  const navigate = route.useNavigate();

  useRememberViewedRow('zonalStatementZms', userId);

  const statementQuery = useQuery(
    specimenStatementQueryOptions(userId, { dateFrom: search.dateFrom, dateTo: search.dateTo }),
  );
  const statement = statementQuery.data;
  const columns = useMemo(() => getSpecimenStatementColumns(), []);

  return (
    <DetailTableLayout
      title={
        statementQuery.isLoading
          ? undefined
          : getStatementTitle(STATEMENT_NAME, statement?.member.name)
      }
      titleAddon={
        <DateRangeBadge
          dateFrom={search.dateFrom ?? statement?.period.dateFrom}
          dateTo={search.dateTo ?? statement?.period.dateTo}
        />
      }
      onNoHistory={() => void navigate({ to: '/dashboards/zonal-statement', search })}
      stats={getSpecimenStatCards(statement)}
      table={{
        rows: statement?.items ?? [],
        columns,
        isLoading: statementQuery.isLoading,
        error: statementQuery.error,
        errorTitle: 'Could not load the specimen statement',
        onRetry: () => void statementQuery.refetch(),
        stickyColumnId: 'name',
        emptyMessage: 'No specimen is flagged for closing in this period',
      }}
    />
  );
}
